import { Component, ElementRef, HostListener, Input, OnDestroy, OnInit, Renderer2, ViewChild } from "@angular/core";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { AlertController, ModalController } from "@ionic/angular";
import { OidcSecurityService } from "angular-auth-oidc-client";
import Quill from "quill";
import { getCleanedHtml } from "../../quill-languagetool";
import { Observable } from "rxjs";
import { Textvorlagen } from "../api/models/textvorlagen";
import { TextkorrekturService } from "../api/services/textkorrektur.service";
import { TextvorlagenService } from "../api/services/textvorlagen.service";
import { PlatzhalterAutofillInfoModalComponent } from "../util/components/platzhalter-autofill-info-modal/platzhalter-autofill-info-modal.component";
import { KundenterminStatus, TerminBerichtUrgentStatus, TerminRueckfrageAnStatus } from "../util/enums/global-enums";
import { translateQuillEditorElements } from "../util/global-functions/global-functions";
import { TerminService } from "../util/services/termin.service";
import { ThemeService } from "../util/services/theme.service";

interface TextvorlagenOption {
  id: number;
  value: string;
  label: string;
}

@Component({
  selector: "app-textvorlage",
  templateUrl: "./textvorlage.component.html",
  styleUrls: ["./textvorlage.component.scss"],
  standalone: false,
})
export class TextvorlageComponent implements OnInit, OnDestroy {
  @Input() content;
  @Input() field;
  @Input() allowTextbausteinEditing: boolean = true;
  @Input() useResult: boolean = true;
  @Input() zuordnung: string;
  @Input() enableFormatting: boolean = false;
  formGroup: UntypedFormGroup;
  username;
  textvorlagen: Textvorlagen[];
  textvorlagenOptions: TextvorlagenOption[];
  saveAsTemplateDisabled = true;
  cursorPosition = null;
  quillEditor: Quill = null;
  korrigierterText = null;
  korrekturVorschau = null;
  isTextvorlagenUpdate = false;
  correctionMode: "ki" | "languagetool" = "ki";
  omniPolyUrl: SafeResourceUrl;
  omniPolyLoaded = false;
  showOmniPoly = false;
  @ViewChild("omniPolyFrame") omniPolyFrame: ElementRef<HTMLIFrameElement>;
  private messageListener: (event: MessageEvent) => void;

  emailStatusDropdownOptions: {
    value: string | null;
    label: string;
  }[] = [
    ...this.terminService.buildTerminOptions({
      enumObj: KundenterminStatus,
      labels: this.terminService.kundenterminLabels,
      prefix: "Kundentermin:",
      includeNullOption: true,
      nullText: "für alle E-Mails verfügbar",
    }),
    ...this.terminService.buildTerminOptions({
      enumObj: TerminRueckfrageAnStatus,
      labels: this.terminService.rueckfrageAnLabels,
      prefix: "Rückfrage AN:",
    }),
    ...this.terminService.buildTerminOptions({
      enumObj: TerminBerichtUrgentStatus,
      labels: this.terminService.berichtUrLabels,
      prefix: "Bericht ur:",
    }),
  ];

  quillEditorModules: any;

  constructor(
    private modalController: ModalController,
    private fb: UntypedFormBuilder,
    private oidcSecurityService: OidcSecurityService,
    private textvorlagenService: TextvorlagenService,
    private alertController: AlertController,
    private textkorrekturService: TextkorrekturService,
    private terminService: TerminService,
    protected renderer: Renderer2,
    private sanitizer: DomSanitizer,
    private themeService: ThemeService,
  ) {
    this.omniPolyUrl = this.sanitizer.bypassSecurityTrustResourceUrl("assets/omnipoly/index.html");
  }

  ngOnInit() {
    const token = this.oidcSecurityService.getToken();
    this.quillEditorModules = {
      toolbar: [
        [{ header: [1, 2, 3, false] }],
        ["bold", "italic", "underline", "strike"],
        [{ color: [] }, { background: [] }],
        [{ list: "ordered" }, { list: "bullet" }],
        ["link"],
        ["clean"],
      ],
      languageTool: {
        server: "/api/languagetool/inline",
        language: "de-AT",
        cooldownTime: 3000,
        showLoadingIndicator: true,
        disableNativeSpellcheck: false,
        apiOptions: { token },
      },
    };

    this.username = this.oidcSecurityService.getPayloadFromIdToken().sb;
    this.formGroup = this.fb.group({
      fg_content: this.content,
      template_name: null,
      template_zuordnung: null,
      selected_vorlage: null,
      fg_content_korrektur: this.korrigierterText,
    });
    this.getTextvorlagen();

    this.formGroup.valueChanges.subscribe((v) => {
      this.saveAsTemplateDisabled =
        v.template_name === "" || v.template_name === null || v.fg_content === null || v.fg_content === "";
    });

    this.formGroup.controls.template_name.valueChanges.subscribe(() => {
      this.updateIsTextvorlagenUpdate();
    });

    this.messageListener = (event: MessageEvent) => {
      if (event.data?.type === "TEXT_RESULT") {
        this.formGroup.controls.fg_content.patchValue(event.data.text);
        this.formGroup.markAsDirty();
        this.showOmniPoly = false;
      }
    };
    window.addEventListener("message", this.messageListener);
  }

  ngOnDestroy() {
    if (this.messageListener) {
      window.removeEventListener("message", this.messageListener);
    }
  }

  private get currentTextvorlage() {
    const textvorlageName = this.formGroup.controls.template_name.value;
    return this.textvorlagen.find((e) => e.name === textvorlageName);
  }

  private updateIsTextvorlagenUpdate() {
    this.isTextvorlagenUpdate = this.currentTextvorlage !== undefined;
  }

  private getTextvorlagen() {
    this.textvorlagenService
      .textvorlagenList({
        benutzer: this.username,
        kategorie: this.field.toLowerCase(),
      })
      .subscribe((textvorlagen: Textvorlagen[]) => {
        this.textvorlagen = textvorlagen.sort((a, b) => {
          const nameA = a.name?.toLowerCase() ?? "";
          const nameB = b.name?.toLowerCase() ?? "";

          if (nameA < nameB) {
            return -1;
          }
          if (nameA > nameB) {
            return 1;
          }
          return 0;
        });

        if (this.zuordnung) {
          this.textvorlagen = this.textvorlagen.filter(
            (vorlage) => vorlage.zuordnung?.toLowerCase() === this.zuordnung?.toLowerCase() || !vorlage.zuordnung,
          );
        }

        this.textvorlagenOptions = this.textvorlagen.map((textvorlage) => {
          return {
            id: textvorlage.id,
            value: textvorlage.inhalt,
            label: `${textvorlage.name} - ${textvorlage.inhalt}`,
          } as TextvorlagenOption;
        });
      });
  }

  uebernehmen() {
    const rawContent = this.formGroup.controls.fg_content.value;
    const cleanedContent = this.useQuillEditor ? getCleanedHtml(rawContent) : rawContent;
    this.modalController.dismiss({
      keep: true,
      content: cleanedContent,
    });
  }

  async cancelModal() {
    if (this.formGroup.dirty) {
      const alert = await this.alertController.create({
        cssClass: "loeschen",
        header: "Verwerfen",
        message: "Ungespeicherte Änderungen wirklich verwerfen?",
        buttons: [
          {
            text: "Abbrechen",
            role: "cancel",
            cssClass: "color-primary",
          },
          {
            cssClass: "color-danger",
            text: "Verwerfen",
            handler: () => {
              this.modalController.dismiss({ keep: false, content: "" });
            },
          },
        ],
      });
      await alert.present();
    } else {
      this.modalController.dismiss({ keep: false, content: "" });
    }
  }

  updateTextVorlage() {
    const content = this.formGroup.controls.fg_content.value;
    const templateName = this.formGroup.controls.template_name.value;
    const templateZuordnung = this.formGroup.controls.template_zuordnung?.value ?? null;

    const params = {
      benutzer: this.username,
      inhalt: content,
      name: templateName,
      kategorie: this.field.toLowerCase(),
      zuordnung: templateZuordnung,
    };
    if (content !== "" && templateName !== null && templateName !== "") {
      if (this.isTextvorlagenUpdate) {
        const updateParams = { id: this.currentTextvorlage.id, data: params };
        this.textvorlagenService.textvorlagenUpdateResponse(updateParams).subscribe((d) => {
          this.getTextvorlagen();
          if (!this.useResult) {
            this.modalController.dismiss(null, "confirm");
          }
        });
      } else {
        this.textvorlagenService.textvorlagenCreate(params).subscribe((d) => {
          this.getTextvorlagen();
          if (!this.useResult) {
            this.modalController.dismiss(null, "confirm");
          }
        });
      }
    }
  }

  vorlageEinfuegen() {
    const selected = this.formGroup.controls.selected_vorlage?.value?.id;
    const vorlageObj = this.textvorlagen.filter((f) => {
      return f.id === selected;
    })[0];
    if (this.useQuillEditor) {
      const caretPosition = this.quillEditor.getSelection(true);
      this.quillEditor.clipboard.dangerouslyPasteHTML(caretPosition.index, vorlageObj.inhalt);
      this.formGroup.controls.fg_content.patchValue(this.quillEditor.root.innerHTML);
    } else {
      if (this.formGroup.controls.fg_content.value) {
        const cursorPosition = this.cursorPosition ?? this.formGroup.controls.fg_content.value.length;
        const newContent = [
          this.formGroup.controls.fg_content.value.slice(0, cursorPosition),
          vorlageObj.inhalt,
          this.formGroup.controls.fg_content.value.slice(cursorPosition),
        ].join("");
        this.formGroup.controls.fg_content.patchValue(newContent);
      } else {
        this.formGroup.controls.fg_content.patchValue(vorlageObj.inhalt);
      }
    }
    this.refreshTextbausteinData();
  }

  refreshTextbausteinData() {
    const selected = this.formGroup.controls.selected_vorlage?.value?.id;
    const vorlageObj = this.textvorlagen.filter((f) => {
      return f.id === selected;
    })[0];
    this.formGroup.controls.template_name.patchValue(vorlageObj?.name ?? null);
    this.formGroup.controls.template_zuordnung.patchValue(vorlageObj?.zuordnung ?? null);
  }

  async deleteVorlage() {
    const alert = await this.alertController.create({
      header: "Textbaustein wirklich löschen?",
      message:
        "Bitte bestätigen Sie das Löschen des Textbausteins. Der Textbaustein steht Ihnen nach dem Löschen nicht mehr zur Verfügung!",
      buttons: [
        {
          text: "Abbrechen",
          role: "cancel",
          cssClass: "color-primary",
        },
        {
          cssClass: "color-danger",
          text: "Löschen",
          handler: () => {
            this.textvorlagenService
              .textvorlagenDelete(this.formGroup.controls.selected_vorlage.value.id)
              .subscribe((p) => {
                this.getTextvorlagen();
                this.formGroup.controls.selected_vorlage.patchValue(null);
                this.formGroup.controls.template_name.patchValue(null);
              });
          },
        },
      ],
    });

    await alert.present();
  }

  getPos($event, textAreaInput) {
    this.cursorPosition = $event.target.selectionEnd;
  }

  async korrigiereText() {
    const val = this.formGroup.controls.fg_content.value;
    this.korrekturVorschau = null;
    this.korrigierterText = null;
    this.textkorrekturService
      .textkorrekturCreate({ Txt_Raw: val, token: this.oidcSecurityService.getToken() })
      .subscribe((res) => {
        this.formGroup.controls.fg_content_korrektur.patchValue(res.Txt_Corrected);
        this.content = res.Txt_Raw;
        this.korrigierterText = res.Txt_Corrected;
        this.korrekturVorschau = JSON.parse(res.Txt_Corrected_Vorschau);
        this.scrollToKorrekturVorschau();
      });
  }

  scrollToKorrekturVorschau(): void {
    setTimeout(() => {
      const element = document.getElementById("korrektur-vorschau-target");
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }, 100);
  }

  korrekturUebernehmen() {
    this.formGroup.patchValue({
      fg_content: this.korrigierterText,
      fg_content_korrektur: null,
    });
    this.korrigierterText = null;
    this.korrekturVorschau = null;
  }

  korrekturVerwerfen() {
    this.formGroup.patchValue({
      fg_content_korrektur: null,
      fg_content: this.content,
    });
    this.korrigierterText = null;
    this.korrekturVorschau = null;
  }

  async openTextbausteinPlatzhalterModal() {
    const modal = await this.modalController.create({
      component: PlatzhalterAutofillInfoModalComponent,
      cssClass: "smaller-modal",
    });

    await modal.present();
  }

  switchCorrectionMode(mode: "ki" | "languagetool") {
    this.correctionMode = mode;
    if (mode === "ki") {
      this.showOmniPoly = false;
    } else {
      this.korrigierterText = null;
      this.korrekturVorschau = null;
    }
  }

  korrigiereTextLanguageTool() {
    this.showOmniPoly = true;
    // Wait a tick for the iframe to render, then send text once loaded
    setTimeout(() => this.sendTextToOmniPoly(), 100);
  }

  onOmniPolyLoad() {
    this.omniPolyLoaded = true;
    this.sendTokenToOmniPoly();
    this.sendThemeToOmniPoly();
    this.sendTextToOmniPoly();
  }

  sendTextToOmniPoly() {
    if (this.omniPolyFrame?.nativeElement?.contentWindow && this.omniPolyLoaded) {
      const raw = this.formGroup.controls.fg_content.value || "";
      const text = this.stripHtml(raw);
      this.omniPolyFrame.nativeElement.contentWindow.postMessage({ type: "SET_TEXT", text }, "*");
    }
  }

  sendTokenToOmniPoly() {
    if (this.omniPolyFrame?.nativeElement?.contentWindow && this.omniPolyLoaded) {
      const token = this.oidcSecurityService.getToken();
      this.omniPolyFrame.nativeElement.contentWindow.postMessage({ type: "SET_TOKEN", token }, "*");
    }
  }

  sendThemeToOmniPoly() {
    if (this.omniPolyFrame?.nativeElement?.contentWindow && this.omniPolyLoaded) {
      const theme = this.themeService.enableDarkmode ? "dark" : "light";
      this.omniPolyFrame.nativeElement.contentWindow.postMessage({ type: "SET_THEME", theme }, "*");
    }
  }

  private stripHtml(html: string): string {
    const doc = new DOMParser().parseFromString(html, "text/html");
    return doc.body.textContent || "";
  }

  omniPolyUebernehmen() {
    if (this.omniPolyFrame?.nativeElement?.contentWindow) {
      this.omniPolyFrame.nativeElement.contentWindow.postMessage({ type: "GET_TEXT" }, "*");
    }
  }

  @HostListener("window:beforeunload")
  canDeactivate(): Observable<boolean> | boolean {
    return !this.formGroup.dirty;
  }

  protected readonly translateQuillEditorElements = translateQuillEditorElements;

  get isEmail() {
    return this.field === "E-Mail";
  }

  get useQuillEditor() {
    return this.isEmail || this.enableFormatting;
  }

  quillOnEditorCreated(quill: Quill) {
    this.quillEditor = quill;
  }
}
